import React from 'react';
import { API } from '../../services';
import { createUrlState } from '../../services/QueryParameterService';
import handleSubtitles from '../../helpers/SearchInputSubtitles';
import generateUUID4 from '../../shared/utils/UuId4Helper';
import * as types from '../types/hotels';
import { WidgetConfigHelper } from 'Helpers';

function getHotelsListStart() {
    return { type: 'HOTEL_LIST_START' };
}

function getHotelsListSuccess(response) {
    return { type: 'HOTEL_LIST_SUCCESS', payload: response };
}

function getHotelsListFailure(error) {
    return { type: 'HOTEL_LIST_ERROR', payload: error.message };
}

function setCurrentPage(page) {
    return { type: 'SET_CURRENT_PAGE_PAGINATION', payload: page };
}

function setCurrentPageSize(pageSize) {
    return { type: 'SET_PAGE_SIZE', payload: pageSize };
}

function resetPaginationAction() {
    return { type: 'RESET_PAGINATION' };
}

function cleanPagesAction() {
    return { type: 'CLEAN_PAGES' };
}

function changeDisplayMode(mode) {
    return { type: 'CHANGE_DISPLAY_MODE', payload: mode };
}

function setPropertiesPricesRange(priceRangeMin, priceRangeMax) {
    return { type: types.SET_PROPERTIES_PRICES_RANGE, payload: { priceRangeMin, priceRangeMax } };
}

// FIXME: Im letting the unused formal param id to avoid possible breaks due to positional parameters.
// eslint-disable-next-line no-unused-vars
export const getHotelsList = (params, id = '', triggeredBy = '') => async (dispatch, getState) => {
    dispatch(getHotelsListStart());
    try {
        const state = getState();
        const { isFirstTime, showAlternativeOffers } = state.hotels;

        if (isFirstTime) {
            dispatch({ type: types.SET_IS_FIRST_TIME });
        }

        const { widgetType, hotelsFilters, alternativeOffers } = state.settings.data;

        // Filters can be set either by destinations widget or by widget setting. Next walks through both paths, prioritizing
        // widget decision

        const selectedDestinationFilter = state.destinations?.selected?.value;

        const filtersPresent = (selectedDestinationFilter) ?? (hotelsFilters);

        let countryCode = filtersPresent?.countryCode || selectedDestinationFilter || '';

        // Sometimes the filter may come bad coded from CMS, this fixes it to make the request properly
        countryCode = countryCode.replace('&amp;', '&');

        let city = filtersPresent && filtersPresent.city ? filtersPresent.city : '';

        city = city.replace('&amp;', '&');

        const hotelsParams = WidgetConfigHelper.buildHotelsListParams(params, state);

        if (countryCode) {
            hotelsParams.countryCode = countryCode;
        }

        if (city) {
            hotelsParams.city = city;
        }

        if (showAlternativeOffers) {
            const { alternativeCheckinCount, alternativeCheckinDaysOfWeek, displayArrivalsWithNoAvail } = alternativeOffers;
            hotelsParams.alternativeCheckinCount = alternativeCheckinCount;
            hotelsParams.alternativeCheckinDaysOfWeek = alternativeCheckinDaysOfWeek;
            hotelsParams.displayArrivalsWithNoAvail = displayArrivalsWithNoAvail;
        }

        const { channelId } = hotelsParams;

        const hotelsList = widgetType === 'KUBE'
            ? await API.getHotelsList(channelId, hotelsParams)
            : await API.getEeHotelsList(hotelsParams);

        await dispatch(changeDisplayMode(hotelsParams.priceDisplayMode));

        if (isFirstTime) {
            await dispatch(setPropertiesPricesRange(
                hotelsList.priceRangeMin || 0,
                hotelsList.priceRangeMax || 0,
            ));
        }

        return dispatch(getHotelsListSuccess(hotelsList));
    } catch (error) {
        return dispatch(getHotelsListFailure(error));
    }
};

export const setPage = page => (dispatch, getState) => {
    dispatch(setCurrentPage(page));

    const newUrlState = createUrlState(getState(), false, true);
    const pages = (getState().hotels.data && getState().hotels.data.pages)
    || getState().hotels.properties.pages;

    return Promise.resolve({
        newUrlState,
        pages,
    });
};

export const setPageSize = pageSize => (dispatch, getState) => {
    dispatch(setCurrentPageSize(pageSize));

    const newUrlState = createUrlState(getState(), false, true);

    return Promise.resolve({ newUrlState });
};

export const resetPagination = () => (dispatch) => {
    dispatch(resetPaginationAction());
    return Promise.resolve();
};

export const cleanPages = () => (dispatch) => {
    dispatch(cleanPagesAction());
    return Promise.resolve();
};

export const sortProperties = value => (dispatch) => {
    dispatch({ type: types.SORT_PROPERTIES, payload: value === 'recommended' ? '' : value });
};

function getSuggestionsRequestStart() {
    return { type: types.GET_SUGGESTIONS_REQUEST_START };
}

function getSuggestionsRequestSuccess() {
    return { type: types.GET_SUGGESTIONS_REQUEST_SUCCESS };
}

function getSuggestionsRequestFailure() {
    return { type: types.GET_SUGGESTIONS_REQUEST_FAILURE };
}

const generateGoogleMapsToken = () => (dispatch) => {
    const sessionToken = generateUUID4();
    dispatch({ type: types.CHANGE_GOOGLE_MAPS_SESSION_TOKEN, payload: sessionToken });
    return sessionToken;
};

export const getSuggestions = valueSearchInput => (dispatch, getState) => {
    dispatch(getSuggestionsRequestStart());

    const { settings, search, hotels } = getState();
    const { channelId, language, autocomplete } = settings.data;
    const { userAgent } = search.data;

    const customClassificationItems = hotels?.classifications?.autocomplete?.autocompleteItemsForRq;
    const customClassificationData = hotels?.classifications?.autocomplete?.autocompleteItemsData;
    const googleMapsSessionToken = hotels?.googleMapsSessionToken || dispatch(generateGoogleMapsToken());

    const params = {
        customClassificationItems,
        channelId,
        user: {
            language,
            userAgent,
        },
        types                        : autocomplete?.types || [ 'CUSTOM_CLASSIFICATION' ],
        customClassificationsFilters : autocomplete?.customClassificationsFilters,
        input                        : valueSearchInput,
        googleMapsSessionToken,
    };
    return API.getSuggestions(params)
        .then((response) => {
            response.categories.forEach((category) => {
                if (category.type === 'classifications') {
                    category.children
                        .forEach((item) => {
                            /* eslint-disable */
                            item.title = (
                                <span className="property-filter-with-quantity-badge-wrapper">
                                    <span>
                                        { item.title }
                                    </span>
                                </span>
                            );
                            item.number = customClassificationData[item.value]?.number;
                            item.subtitle = customClassificationData[item.value]?.categoryName;
                            /* eslint-enable */
                        });
                }
            });

            const responseWithSubtitles = handleSubtitles(response);

            dispatch(getSuggestionsRequestSuccess());
            return Promise.resolve(responseWithSubtitles);
        })
        .catch((err) => {
            dispatch(getSuggestionsRequestFailure());
            return Promise.resolve(err);
        });
};

export const changePropertiesByCountryCode = country => dispatch =>
    dispatch({ type: types.CHANGE_PROPERTY_COUNTRY_FILTER, payload: Array.isArray(country) ? country : [ country ] });

export const changePropertiesByCode = propertyCode => dispatch =>
    dispatch({ type: types.CHANGE_PROPERTY_CODE_FILTER, payload: propertyCode });

export const changePropertiesByLocation = location => dispatch =>
    dispatch({ type: types.CHANGE_PROPERTY_LOCATION_FILTER, payload: location });

export const changePropertiesByClassification = fullClassificationId => dispatch => dispatch({
    type    : types.CHANGE_PROPERTY_CLASSIFICATION_FILTER,
    payload : fullClassificationId,
});

export const changePropertiesByGeoPosition = latLong => (dispatch, getState) => {
    const autocomplete = getState().settings.data?.autocomplete;
    let payload = { ...latLong };

    if (Object.keys(autocomplete)?.length) {
        const {
            citySearchDistanceBucketsKm,
            citySearchDistanceKm,
            citySearchDistanceBucketsMiles,
            citySearchDistanceMiles,
            citySearchBucketsStopCount,
            citySearchDistanceMode,
        } = autocomplete;

        payload = {
            ...payload,
            citySearchDistanceBucketsKm,
            citySearchDistanceKm,
            citySearchDistanceBucketsMiles,
            citySearchDistanceMiles,
            citySearchBucketsStopCount,
            citySearchDistanceMode,
        };
    }

    dispatch({ type: types.SET_GEO_FILTERS, payload });
};

export const changePropertiesByGeoName = geoRegionId => dispatch => dispatch(({ type: types.SET_GEO_FILTERS, payload: { geoRegionId } }));

export const resetPropertiesByGeoPosition = () => dispatch => dispatch({ type: types.RESET_GEO_FILTERS });

export const changePriceFilter = (minPrice, maxPrice) => (dispatch) => {
    dispatch({ type: types.CHANGE_PRICE_FILTER, payload: { minPrice, maxPrice } });
    return Promise.resolve();
};

export const changeCheckboxesFilter = (filterName, value) => (dispatch) => {
    const typeName = `CHANGE_${filterName.toUpperCase()}_FILTER`;

    if (filterName === 'classification' || filterName === 'awards') {
        return dispatch({ type: types[typeName], payload: value });
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
        return dispatch({ type: types[typeName], payload: value.value });
    }

    return dispatch({ type: types[typeName], payload: value });
};

export const setAutocompleteFilter = (item, widgetId, widgetType) => (dispatch) => {
    let label;
    if (widgetType === 'searchbar') {
        if (item?.title?.props) {
            label = item.title.props.children.props.children;
        } else if (item?.title) {
            label = item.title;
        } else {
            label = '';
        }
    }

    if (widgetType === 'hotels') {
        label = item?.props ? item.props.children.props.children : item;
    }
    dispatch({
        type             : types.SET_AUTOCOMPLETE_FILTER,
        payload          : label,
        id               : widgetId,
        autocompleteData : widgetType === 'searchbar' ? {
            type  : item?.type,
            value : label,
            code  : item?.value,
        } : undefined,
    });
};

export const cleanAutocompleteFilters = widgetId => (dispatch) => {
    dispatch({ type: types.RESET_AUTOCOMPLETE_FILTERS, id: widgetId });
};

export const changePropertyNameFilterTemp = propertyName => (dispatch) => {
    dispatch({ type: types.CHANGE_PROPERTY_NAME_FILTER_TEMP, payload: propertyName });
    return Promise.resolve();
};

export const getAssociatedCountries = () => async (dispatch, getState) => {
    const { domain, user } = WidgetConfigHelper.buildHotelsListParams(null, getState());
    const countries = await API.getAssociatedCountries({ domain, user });
    if (countries) {
        dispatch({ type: types.SET_ASSOCIATED_COUNTRIES, payload: countries });
        return Promise.resolve(countries);
    }

    return Promise.reject();
};

export const setClassifications = (language, channelId, widgetId) => dispatch => API
    .getClassifications({ languageCode: language, channelId })
    .then((response) => {
        dispatch({
            type    : types.SET_CLASSIFICATIONS,
            payload : response,
            id      : widgetId,
        });
        return Promise.resolve();
    });

export const loadClassifications = language => (dispatch, getState) => {
    const param = WidgetConfigHelper.buildHotelsListParams({}, getState());
    return setClassifications(language, param.channelId, '')(dispatch);
};

export const applyCoupon = code => (dispatch) => {
    dispatch({ type: types.APPLY_COUPON, payload: code });
};

export const getGooglePlaces = (geoRegionId, type) => (dispatch, getState) => {
    const { settings: { data: { channelId } }, hotels: { googleMapsSessionToken } } = getState();
    const params = {
        channelId,
        googleMapsSessionToken,
        geoRegionId,
        type,
    };
    return API.getGooglePlaces(params);
};

export const getPortalFilters = languageCode => async (dispatch, getState) => {
    const { channelId } = WidgetConfigHelper.buildHotelsListParams({}, getState());
    const data = await API.getPortalFilters(channelId, languageCode);
    dispatch({ type: types.PORTAL_FILTERS, payload: data });
};

export const toggleAlternativeOffersShown = () => async (dispatch) => {
    dispatch({ type: types.TOGGLE_ALTERNATIVE_OFFERS_SHOWN });
};
